import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { attachJob, selectJobByCourse } from './jobsSlice'
import { progressFromResult } from './lib/jobProgress'
import type { StudioJob } from './studioTypes'

/**
 * Цепочка §7.4: после `success` перевода бэк САМ ставит джобу на картинки.
 *
 * Перед перецеплением джоба перевода перечитывается ещё раз, и это не
 * перестраховка: перевод коммитится ДО вызова вебхука картинок, поэтому
 * опрос, попавший в промежуток, успевает увидеть `next_job_id`, перецепиться
 * — и показать `failed` у джобы, которую сервер закрыл сам и деньги за
 * которую уже вернул. Источник правды — повторно прочитанный `result` самой
 * джобы перевода, а не тот, что был на момент первого `success`.
 *
 * `confirmed` — «повторное чтение уже вернулось» (в `JobPoller` это
 * сравнение `fulfilledTimeStamp` подписки ДО и ПОСЛЕ вызова `refetch`).
 * Без него функция была бы неотличима от «вызвали refetch и тут же
 * поверили старым данным» — оба теста цепочки проверяют именно порядок
 * «сначала перечитали, потом решаем», а не сам факт вызова.
 *
 * Упавшее (сетевое) `refetch()` цепочку не хоронит: ниже мы просим ОДИН
 * раз (латч на факт вызова, а не на успех), но `JobPoller` намеренно не
 * останавливает штатный опрос этой же джобы, пока перевод не подтверждён —
 * следующий тик поллера бьёт по тому же `ai/jobs/{id}` и подтвердит чтение
 * сам, даже если наш явный вызов не долетел. Это НЕ автоповтор ЦЕПОЧКИ
 * (перезапуск джобы), которого §7.4 запрещает, — это обычный повторный GET
 * уже идущего опроса, тот же класс операции, что и любой пропущенный тик.
 */
export const useTranslationChain = (
  courseId: number,
  job: StudioJob | undefined,
  refetch: () => void,
  confirmed = false,
): void => {
  const dispatch = useAppDispatch()
  // Оба флага держат идемпотентность НА ЭКЗЕМПЛЯР подписки: если джоба того
  // же курса сменится (новый `job.id`), поллер размонтирует эту подписку и
  // смонтирует новую — эффекты начнут жизнь заново, повторного refetch/attach
  // тем же job.id тут не бывает.
  const refetched = useRef(false)
  const attached = useRef(false)
  // Свежая запись реестра курса — читаем СЕЛЕКТОРОМ, а не из замыкания
  // эффекта: гонка с ручным «показать прогон» (M-2, ревью раунд 1). Окно
  // меньше секунды, но проверка перед диспатчем дешёвая — ref нужен только
  // чтобы эффект видел значение АКТУАЛЬНОГО рендера, а не своего замыкания.
  const registryEntry = useAppSelector((s) => selectJobByCourse(s, courseId))
  const registryEntryRef = useRef(registryEntry)
  registryEntryRef.current = registryEntry

  const isDoneTranslation = job?.type === 'course_translation' && job.status === 'success'

  useEffect(() => {
    if (!isDoneTranslation || refetched.current) return
    refetched.current = true
    refetch()
  }, [isDoneTranslation, refetch])

  useEffect(() => {
    if (!isDoneTranslation || !confirmed || attached.current) return
    const result = job.result
    const nextJobId = result?.next_job_id
    // Три ветки «цепочки не будет» (рисовать нечего; не хватило кредитов;
    // воркфлоу не разбудили) выглядят на фронте одинаково — без
    // `next_job_id`. Ни одна из них не отменяет успеха перевода: прогон
    // просто закрывается, реестр не трогаем.
    if (!nextJobId || !result) return
    // Гонка с ручным «показать прогон» (M-2): за окно между подтверждённым
    // чтением и этим диспатчем автор мог переключить реестр курса на другую
    // джобу. Цепляемся, только если запись всё ещё указывает на ТУ ЖЕ джобу
    // перевода, из которой растёт цепочка — иначе цепочка молча перетёрла бы
    // чужой осознанный выбор.
    const registered = registryEntryRef.current
    if (!registered || registered.jobId !== job.id || registered.type !== 'course_translation') {
      return
    }
    attached.current = true
    dispatch(
      attachJob({
        courseId,
        entry: {
          jobId: nextJobId,
          type: 'course_image_generation',
          startedAt: Date.now(),
          params: { kind: 'course_image_generation', targets: null, pilot_sets: 0 },
          total: null,
          // Явный факт цепочки (финальное ревью, п.3) — гвард
          // `isStartedTranslationRun` читает ЭТО поле, а не `previous.type`,
          // которое `useResumeJob` перезапишет целиком, если эта картиночная
          // половина оборвётся и её продолжат (§8).
          translationChain: true,
          // Оценка картинок со старта — полосе до первого `images_total`.
          ...(registered.imagesTotal ? { imagesTotal: registered.imagesTotal } : {}),
          previous: {
            jobId: job.id,
            type: 'course_translation',
            kind: 'chain',
            progress: progressFromResult('course_translation', result),
            skipped: result.skipped,
            skipped_codes: result.skipped_codes,
            // Итог перевода по видам — иначе после перецепления на картинки
            // «сколько уроков и слайдов переведено» взять неоткуда.
            translated: result.translated,
          },
        },
        // Это продолжение ТОГО ЖЕ разговора, а не новый прогон (§7.4) — см.
        // исключение в `chatSlice` (второй законный вызывающий — §8, явное
        // продолжение прерванного прогона в `useResumeJob`).
        continuesChat: true,
      }),
    )
  }, [isDoneTranslation, confirmed, job, courseId, dispatch])
}
